import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card, Button, Skeleton, Dropdown, Menu, Modal, Typography, Grid, Tabs, Layout
} from 'antd';
import {
  MoreOutlined, EditOutlined, IdcardOutlined, UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import fetchPatient from '../utils/patient/fetchPatient';
import fetchSessionsList from '../utils/session/fetchSessionsList';
import deletePatient from '../utils/patient/deletePatient';
import CallList from '../components/call/CallList';
import fetchCallsList from '../utils/call/fetchCallsList';
import BackLink from '../components/BackLink';
import RecordingModal from '../components/session/RecordingModal';
import PatientForm from '../components/patient/PatientForm';
import SessionList from '../components/session/SessionList';
import PatientSummary from '../components/patient/PatientSummary';
import PatientCallButton from '../components/patient/PatientCallButton';
import { toast } from 'react-hot-toast';
import { handleError } from '../utils/errorHandling';
import supportedFeature from '../utils/supportedFeature';
import { logEvent } from '../analytics';

const { Text } = Typography;
const { useBreakpoint } = Grid;
const { Content } = Layout;

export default function PatientDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const { uuid } = useParams();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [sessionPagination, setSessionPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [calls, setCalls] = useState([]);
  const [callPagination, setCallPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [reloadCallsTrigger, setReloadCallsTrigger] = useState(0);

  const [activeTab, setActiveTab] = useState('sessions');

  // sync with URL hash only once after mount
  useEffect(() => {
    const initialTab = location.hash?.substring(1);
    if (initialTab === 'calls' || initialTab === 'sessions') {
      setActiveTab(initialTab);
    }
  }, []);

  // load patient data
  useEffect(() => {
    (async () => {
      try {
        const result = await fetchPatient(uuid);
        setPatient(result);
      } catch {
        handleError(t('patient_details.error_loading'));
        navigate('/sessions#patients', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [uuid]);

  // reload sessions on uuid change
  useEffect(() => {
    loadSessions(sessionPagination.current);
  }, [uuid]);

  // reload calls on uuid or after a call ends
  useEffect(() => {
    loadCalls(callPagination.current);
  }, [uuid, reloadCallsTrigger]);

  const loadSessions = async page => {
    setLoading(true);
    try {
      const { sessions, pagination } = await fetchSessionsList(page, { patient_uuid: uuid });
      setSessions(sessions);
      setSessionPagination(pagination);
    } catch {
      handleError(t('patient_details.error_loading'));
      navigate('/sessions#patients', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const loadCalls = async page => {
    setLoading(true);
    try {
      const { calls, pagination } = await fetchCallsList(page, { patient_uuid: uuid });
      setCalls(calls);
      setCallPagination(pagination);
    } catch {
      handleError(t('patient_details.error_loading'));
      navigate('/sessions#patients', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    logEvent('PatientDetails', 'Edit');
    setEditing(true);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t('patient_details.confirm_delete_title'),
      content: t('patient_details.confirm_delete_message'),
      okText: t('patient_details.confirm_delete_ok'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deletePatient(uuid);
          logEvent('PatientDetails', 'Delete');
          toast.success(t('patient_details.deleted_success'));
          navigate('/sessions#patients');
        } catch {
          toast.error(t('patient_details.delete_error'));
        }
      },
    });
  };

  const handleTabChange = key => {
    setActiveTab(key);
    navigate(`#${key}`, { replace: true });
  };

  // Switch to "calls" tab on call end and then reload
  const handleCallEnded = async () => {
    // first switch the tab and URL
    setActiveTab('calls');
    navigate('#calls', { replace: true });

    // then wait for backend to settle, and bump our trigger
    await new Promise(r => setTimeout(r, 1000));
    setReloadCallsTrigger(n => n + 1);
  };

  const menu = (
    <Menu>
      <Menu.Item key="delete" danger onClick={handleDelete}>
        {t('patient_details.delete')}
      </Menu.Item>
    </Menu>
  );

  if (loading || !patient) {
    return (
      <Content className='page'>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Content>
    );
  }

  const cardTitle = (
    <div
      style={{
        padding: '15px 0',
        display: 'flex',
        flexDirection: screens.md ? 'row' : 'column',
        alignItems: screens.md ? 'center' : 'stretch',
        justifyContent: 'space-between',
        gap: screens.md ? 8 : 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <UserOutlined style={{ marginRight: 8 }} />
        <span
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: screens.md ? 500 : '100%', // prevent overflow on large screens
            display: 'inline-block',
          }}
        >
        {patient.getFullName()}
      </span>
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={handleEdit}
          style={{
            color: 'var(--main-color)',
            padding: 0,
            marginLeft: screens.md ? 10 : 0
        }}>
          {t('patient_details.edit')}
        </Button>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: screens.md ? 'flex-start' : 'flex-end',
          marginTop: screens.md ? 0 : 16,
          overflowX: 'auto',
          gap: 8,
          flexWrap: 'nowrap',
          alignItems: 'center', // aligns vertically
          minHeight: 40,        // ensures enough height
        }}
      >
        {supportedFeature('Calls') && (
          <PatientCallButton patient={patient} onCallEnded={handleCallEnded} />
        )}
        <RecordingModal patient={patient} />
        <Dropdown overlay={menu} placement="bottomRight" trigger={['click']}>
          <Button icon={<MoreOutlined />} data-testid="patient-hamburger-button" />
        </Dropdown>
      </div>
    </div>
  );

  return (
    <div className="page patient-details-page">
      <div style={{ marginBottom: 16 }}>
        <BackLink to="/sessions#patients" />
      </div>

      <Card title={cardTitle} bordered>
        <PatientSummary patient={patient} />
      </Card>

      {screens.lg && sessions.length > 0 && (
        <div style={{ color: 'gray', padding: 10, textAlign: 'center' }}>
          <IdcardOutlined className="iconSpacing" />
          <Text type="secondary">{t('patient_details.privacy_info')}</Text>
        </div>
      )}

      {/* build tabs dynamically */}
      {(() => {
        const items = [
          {
            key: 'sessions',
            label: t('patient_details.sessions_tab'),
            children: (
              <SessionList
                sessions={sessions}
                pagination={sessionPagination}
                showHeader
                fromPatientDetails
                onPageChange={loadSessions}
              />
            ),
          },
        ];
        if (supportedFeature("Calls")) {
          items.push({
            key: 'calls',
            label: t('patient_details.calls_tab'),
            children: (
              <CallList
                calls={calls}
                pagination={callPagination}
                fromPatientDetails
                onPageChange={loadCalls}
              />
            ),
          });
        }

        return (
          <Tabs
            type="card"
            activeKey={activeTab}
            onChange={handleTabChange}
            style={{ marginTop: 24 }}
            items={items}
          />
        );
      })()}

      <PatientForm
        isOpen={editing}
        onCancel={() => setEditing(false)}
        onSave={updated => {
          setPatient(updated);
          setEditing(false);
        }}
        editingPatient={patient}
      />
    </div>
  );
}